import { useState, useEffect, useCallback } from "react";

interface Fudge {
  id: string;
  tagId: string | null;
  description: string;
  changeDetail: string;
  location: string | null;
  kks: string | null;
  appliedByName: string;
  appliedAt: string;
  reinstatedByName: string | null;
  reinstatedAt: string | null;
  reinstatedManually: number;
  reappliedFromId: string | null;
  status: string;
}

interface Session {
  user: { id: string; name: string; email: string; role: string };
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease",
        color: "#94a3b8",
      }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function FudgesPage() {
  const [fudges, setFudges] = useState<Fudge[]>([]);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Manual reinstate modal
  const [reinstateTarget, setReinstateTarget] = useState<Fudge | null>(null);
  const [reinstatedByName, setReinstatedByName] = useState("");
  const [reason, setReason] = useState("");
  const [reinstating, setReinstating] = useState(false);
  const [reinstateError, setReinstateError] = useState("");

  const fetchFudges = useCallback(async () => {
    const r = await fetch("/api/fudges");
    if (r.ok) setFudges(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchFudges();
    fetch("/api/auth/get-session").then((r) => r.ok ? r.json() : null).then(setSession);
  }, [fetchFudges]);

  const isAdmin = session?.user?.role === "admin";

  function fmt(iso: string) {
    return new Date(iso).toLocaleString();
  }

  function daysAgo(iso: string) {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  }

  function openReinstate(f: Fudge) {
    setReinstateTarget(f);
    setReinstatedByName(session?.user?.name ?? "");
    setReason("");
    setReinstateError("");
  }

  async function handleManualReinstate(e: React.FormEvent) {
    e.preventDefault();
    if (!reinstateTarget) return;
    setReinstating(true);
    setReinstateError("");
    const r = await fetch(`/api/fudges/${reinstateTarget.id}/reinstate-manual`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reinstatedByName, reason }),
    });
    setReinstating(false);
    if (r.ok) {
      setReinstateTarget(null);
      setReinstatedByName("");
      setReason("");
      fetchFudges();
    } else {
      const j = await r.json();
      setReinstateError(j.error ?? "Failed");
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Active Fudges</h1>
        <p className="text-sm text-slate-500 mt-0.5">Changes currently applied on plant</p>
      </div>

      {/* Manual reinstate modal */}
      {reinstateTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setReinstateTarget(null)}>
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-xl w-full max-w-md mx-4 md:mx-0"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-slate-800 mb-1">Reinstate without tag</h2>
            <p className="text-sm text-slate-500 mb-4">{reinstateTarget.description}</p>
            <form onSubmit={handleManualReinstate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Your Name *</label>
                <input required type="text" value={reinstatedByName}
                  onChange={(e) => setReinstatedByName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Reason *</label>
                <input required type="text" value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Tag damaged — change physically verified removed"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {reinstateError && <p className="text-sm text-red-600">{reinstateError}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setReinstateTarget(null)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
                <button type="submit" disabled={reinstating}
                  className="px-4 py-2 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 disabled:opacity-50">
                  {reinstating ? "Reinstating…" : "Reinstate"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Loading…</p>
        ) : fudges.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">No active fudges — all clear.</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3">Change Detail</th>
                    <th className="text-left px-4 py-3">Location</th>
                    <th className="text-left px-4 py-3">KKS</th>
                    <th className="text-left px-4 py-3">Applied By</th>
                    <th className="text-left px-4 py-3">Applied</th>
                    {isAdmin && <th className="px-4 py-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {fudges.map((f) => (
                    <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900 max-w-xs">
                        {f.description}
                        {f.reappliedFromId && (
                          <span className="ml-2 text-xs text-slate-400">(re-applied)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs">{f.changeDetail}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{f.location ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono">{f.kks ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{f.appliedByName}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        <span title={fmt(f.appliedAt)}>{daysAgo(f.appliedAt)}</span>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openReinstate(f)}
                            className="text-xs text-amber-600 hover:underline whitespace-nowrap">
                            Reinstate without tag
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — tap to expand detail */}
            <div className="md:hidden divide-y divide-slate-100">
              {fudges.map((f) => {
                const isOpen = expandedId === f.id;
                return (
                  <div key={f.id}>
                    {/* Tappable header row */}
                    <div
                      className="px-4 py-4 flex items-start gap-3 cursor-pointer select-none active:bg-slate-50"
                      onClick={() => setExpandedId(isOpen ? null : f.id)}
                    >
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm font-medium text-slate-900 leading-snug">
                          {f.description}
                          {f.reappliedFromId && (
                            <span className="ml-2 text-xs text-slate-400 font-normal">(re-applied)</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {f.appliedByName} · {daysAgo(f.appliedAt)}
                        </p>
                      </div>
                      <div className="pt-0.5">
                        <Chevron open={isOpen} />
                      </div>
                    </div>

                    {/* Expandable detail panel */}
                    <div
                      style={{
                        maxHeight: isOpen ? "400px" : "0px",
                        overflow: "hidden",
                        transition: "max-height 0.22s ease",
                      }}
                    >
                      <div className="px-4 pt-3 pb-4 bg-slate-50 border-t border-slate-100 space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Change Detail</p>
                          <p className="text-sm text-slate-700">{f.changeDetail}</p>
                        </div>
                        {f.location && (
                          <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Location</p>
                            <p className="text-sm text-slate-700">{f.location}</p>
                          </div>
                        )}
                        {f.kks && (
                          <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">KKS</p>
                            <p className="text-sm font-mono text-slate-700">{f.kks}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Applied</p>
                          <p className="text-sm text-slate-700">{fmt(f.appliedAt)}</p>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openReinstate(f); }}
                            className="w-full mt-1 py-2 px-4 bg-amber-50 border border-amber-200 text-xs text-amber-700 font-medium rounded-md text-left">
                            Reinstate without tag
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
